import {
  useHomeBanners,
  useHomeHero,
  useHomePopup,
} from '@/hooks/useHomeImages';
import { useSearchOptions } from '@/hooks/useSearchOptions';
import { SearchModal } from '@/pages/home/components/searchModal/SearchModal';
import { Fuel, Nullable, VehicleModelType } from '@orcar/common';
import {
  Button,
  convertToKST,
  filterInsuranceAtom,
  filterOptionAtom,
  getSiteConfig,
  getSureUserId,
  HomePopup,
  isMobileAtom,
  MODAL_TYPE,
  openSiteLink,
  PAGE_NAME,
  SearchParamsType,
  setStaticDate,
  siteImageUrl,
  splitBoldMarks,
  Styles,
  useGetVehicleModels,
  useRecoil,
} from '@orcar/common-d2c';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HOME_HERO_FALLBACK } from '@/constants/site-content';
import {
  trackBannerClicked,
  trackHomepageViewed,
  trackSearchStarted,
} from '@/utils/analytics';
import RecommendSection from './components/recommendedCar/RecommendSection';
import { MobileOptionSearchModal } from './components/searchModal/MobileOptionSearchModal';
import * as S from './styles';

// ERP 문구에서 **로 감싼 부분을 굵게 보여 준다.
const renderHeadline = (text = '') =>
  splitBoldMarks(text).map((part, index) =>
    part.bold ? (
      <S.Text key={index} $bold>
        {part.text}
      </S.Text>
    ) : (
      <Fragment key={index}>{part.text}</Fragment>
    ),
  );

const HomePage = () => {
  const navigate = useNavigate();
  const { list: heroList, items: heroItems } = useHomeHero();
  const { list: bannerList, items: bannerItems } = useHomeBanners();
  const { items: popupItems } = useHomePopup();
  const hero = heroItems[0];
  // 문구는 받기 전에도 같은 자리를 차지하게 두고 보이지만 않게 한다.
  const heroText = heroList ?? HOME_HERO_FALLBACK;
  const banner = bannerItems[0];
  const sureUserId = getSureUserId();

  const [isModalOpen, setIsModalOpen] = useState<Nullable<string>>(null);

  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
    resetRecoilState: resetFilterOption,
  } = useRecoil(filterOptionAtom);
  const { resetRecoilState: resetFilterInsurance } =
    useRecoil(filterInsuranceAtom);

  const { data: vehicleModelList } = useGetVehicleModels({
    params: {
      pickUpAt: convertToKST(setStaticDate('pickUp')),
      dropOffAt: convertToKST(setStaticDate('dropOff')),
      driverAge: undefined,
      numberOfPeople: undefined,
      drivingExperience: undefined,
      sureUserId: sureUserId ? Number(sureUserId) : undefined,
    },
  });

  const handleSubmitSearch = (params?: SearchParamsType) => {
    trackSearchStarted({
      pickUpDate: params?.pickUpAt,
      dropOffDate: params?.dropOffAt,
    });

    navigate({
      pathname: PAGE_NAME.search,
      search: new URLSearchParams({
        pickUpAt: convertToKST(params?.pickUpAt),
        dropOffAt: convertToKST(params?.dropOffAt),
        driverAge: params?.driverAge || '',
        drivingExperience: params?.drivingExperience || '',
        numberOfPeople: params?.numberOfPeople || '',
      }).toString(),
    });
    if (isMobile) {
      setIsModalOpen(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(null);
  };

  const {
    searchOptions,
    searchOptionIdx,
    setSearchOptionIdx,
    isNextBtnDisabled,
    isCheckTimeSelected,
    indicatorTexts,
    handleClickIndicator,
    handleNextButton,
    handleTabButton,
  } = useSearchOptions({
    onSubmit: handleSubmitSearch,
    onModalClose: handleCloseModal,
  });

  const handleOpenModal = () => {
    setIsModalOpen(isMobile ? MODAL_TYPE.MOBILE_SEARCH : MODAL_TYPE.WEB_SEARCH);
  };

  const handleRecommendCar = (type: string) => {
    const pickUpAt = convertToKST(searchOptions.pickUpDate?.format());
    const dropOffAt = convertToKST(searchOptions.dropOffDate?.format());

    navigate({
      pathname: PAGE_NAME.search,
      search: `?pickUpAt=${pickUpAt}&dropOffAt=${dropOffAt}`,
    });

    if (Fuel.EV === type) {
      setRecoilFilterOption({
        ...recoilFilterOption,
        carFuelList: [Fuel.EV],
        carTypeList: [VehicleModelType.EV],
      });
    } else {
      setRecoilFilterOption({
        ...recoilFilterOption,
        carTypeList: [type],
      });
    }
  };

  useEffect(() => {
    if (location.pathname === '/') {
      resetFilterOption();
      resetFilterInsurance();
      trackHomepageViewed();
    }
  }, [location.pathname]);

  return (
    <S.Wrapper $isMobile={isMobile}>
      <HomePopup
        items={popupItems}
        dismissKey='rainbow_home_popup_dismissed_until'
        accentColor={getSiteConfig().colors.primary}
      />
      <S.BackgroundImageWrapper $isMobile={isMobile}>
        {hero && (
          <img
            src={
              isMobile
                ? siteImageUrl(hero.mobileImage || hero.image, 1080)
                : siteImageUrl(hero.image, 1920)
            }
            alt={hero.alt ?? ''}
            width={isMobile ? '100%' : '1200px'}
            height={isMobile ? '100%' : '380px'}
            style={{
              objectFit: isMobile ? 'cover' : undefined,
              borderRadius: isMobile ? '0' : '10px',
            }}
          />
        )}
      </S.BackgroundImageWrapper>
      <S.Text
        hidden={isModalOpen === MODAL_TYPE.WEB_SEARCH}
        $isMobile={isMobile}
        style={heroList ? undefined : { visibility: 'hidden' }}
      >
        {renderHeadline(heroText.headline)} <br />
        {renderHeadline(heroText.headlineLine2)}
      </S.Text>
      <S.SearchButtonWrapper
        isOpen={isModalOpen === MODAL_TYPE.WEB_SEARCH}
        className='homeSearchBar'
        onClick={handleOpenModal}
        $isMobile={isMobile}
      >
        <S.ButtonText $isMobile={isMobile}>여행 일정을 알려주세요</S.ButtonText>
        <Button
          width={isMobile ? '70px' : '90px'}
          height={isMobile ? '36px' : '48px'}
          borderRadius={isMobile ? '3px' : '6px'}
          font={isMobile ? Styles.NEW_FONT.B1 : Styles.NEW_FONT.H4}
        >
          검색
        </Button>
      </S.SearchButtonWrapper>

      <RecommendSection
        vehicleModelList={vehicleModelList}
        onClick={handleRecommendCar}
        isMobile={isMobile}
      />

      {banner && (
        <>
          {bannerList?.sectionTitle && (
            <S.BannerSection $isMobile={isMobile}>
              <S.SectionTitle>{bannerList.sectionTitle}</S.SectionTitle>
            </S.BannerSection>
          )}

          <S.BannerWrapper $isMobile={isMobile}>
            <S.ImgWrapper>
              <img
                src={
                  isMobile
                    ? siteImageUrl(banner.mobileImage || banner.image, 1080)
                    : siteImageUrl(banner.image, 1920)
                }
                alt={banner.alt ?? ''}
                onClick={() => {
                  trackBannerClicked({ banner_name: banner.id });
                  openSiteLink(banner.link, navigate);
                }}
                style={{ cursor: 'pointer' }}
              />
            </S.ImgWrapper>
          </S.BannerWrapper>
        </>
      )}
      <MobileOptionSearchModal
        showModal={isModalOpen === MODAL_TYPE.MOBILE_SEARCH}
        onClose={handleCloseModal}
        searchOptionIdx={searchOptionIdx}
        setSearchOptionIdx={setSearchOptionIdx}
        searchOptions={searchOptions}
        isNextBtnDisabled={isNextBtnDisabled}
        isCheckTimeSelected={isCheckTimeSelected}
        handleNextBtn={handleNextButton}
        handleTabBtn={handleTabButton}
      />
      <SearchModal
        showModal={isModalOpen === MODAL_TYPE.WEB_SEARCH}
        onClose={() => setIsModalOpen(null)}
        searchOptions={searchOptions}
        handleClickIndicator={handleClickIndicator}
        handleNextButton={handleNextButton}
        searchOptionIdx={searchOptionIdx}
        setSearchOptionIdx={setSearchOptionIdx}
        indicatorTexts={indicatorTexts}
        isNextBtnDisabled={isNextBtnDisabled}
        isCheckTimeSelected={isCheckTimeSelected}
      />
    </S.Wrapper>
  );
};

export default HomePage;
